/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {selectEventOptionsDesign} from '~/CreateFabricInvitation/reducers/eviteEvent/selectors';
import {FabricArt} from '~/common/fabric/types';
import {useVideoControl} from '~/common/hooks/useVideoControls';
import {OverlayAnimation} from '~/common/OverlayAnimation/OverlayAnimation';
import {CardAnimation, CardAnimationRef} from '~/ViewInvitation/common/CardAnimation/CardAnimation';
import {useScrollToMeganav} from '~/ViewInvitation/hooks/useScrollToMeganav';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';

interface PremiumCardWrapperProps {
  designTemplateRef: RefObject<HTMLDivElement | null>;
  fabricArt: FabricArt;
  isWebview?: boolean;
  isViewInvitationPage?: boolean;
  animated?: boolean;
  onCanPlayAnimation?: () => void;
  scrollToPhotos: boolean;
  setAnimationComplete: () => void;
}

export const PremiumCardWrapper = ({
  designTemplateRef,
  fabricArt,
  isWebview,
  isViewInvitationPage = true,
  animated = false,
  onCanPlayAnimation = () => undefined,
  scrollToPhotos,
  setAnimationComplete,
}: PremiumCardWrapperProps) => {
  const invitationRootEl =
    document.querySelector('#inviteRoot') ||
    document.querySelector('.create-flow__invitation-preview');
  const invitationRootTop = invitationRootEl ? (invitationRootEl as HTMLElement).offsetTop : 50;

  const isMediumAndUp = useIsMediumAndUp();
  const effectVideo = useVideoControl();
  const revealVideo = useVideoControl();
  const [overlayTop, setOverlayTop] = useState<string | number>(invitationRootTop);
  const eventOptionsDesign = useSelector(selectEventOptionsDesign, shallowEqual);
  const fabricDesign = useSelector((s: any) => s.fabricDesign, shallowEqual);
  const hasPlayedRef = useRef<boolean>(false);
  const revealFinishedRef = useRef<boolean>(false);
  const firstRender = useRef<boolean>(true);
  const cardAnimationRef = useRef<CardAnimationRef>(null);
  const effectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {reveal_id, overlay_id} = eventOptionsDesign || {};
  const showAnimation = animated || isViewInvitationPage;

  const [display, setDisplay] = useState<string>(''); // EVT-32862: hides overlays to address iOS bug with pledge donor feed modal

  const {play: playEffect = () => undefined, hide: hideEffect = () => undefined} = effectVideo;
  const {play: playReveal = () => undefined} = revealVideo;

  const hasEffect = !!(overlay_id || fabricDesign?.overlayId);
  const hasReveal = !!(reveal_id || fabricDesign?.revealId);

  const waitForReveal = () =>
    new Promise<void>((resolve) => {
      const video = revealVideo?.videoRef.current;
      if (video) {
        const interval = setInterval(() => {
          const {currentTime, duration} = video;
          const progress = currentTime / duration;
          if (progress > 0.55) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve();
          }
        }, 100);

        // For Safari MW Users on low power mode preventing auto-play, set a max timeout of 5 seconds to avoid infinite wait
        const timeout = setTimeout(() => {
          if (!revealFinishedRef.current) {
            clearInterval(interval);
            resolve();
          }
        }, 5000);
      }
    });

  // biome-ignore lint/correctness/useExhaustiveDependencies: This is intentional
  const envelopeAnimation = useCallback(async () => {
    if (!showAnimation) return;
    revealFinishedRef.current = false;

    if (hasReveal) {
      playReveal();
      await waitForReveal();
    }

    cardAnimationRef.current?.play();

    effectTimeoutRef.current = setTimeout(() => {
      if (effectTimeoutRef.current) {
        playEffect();
        effectTimeoutRef.current = null;
      }
    }, 4000);
  }, [showAnimation, hasReveal]);

  const replayAnimation = useCallback(() => {
    // Reset all animation states
    if (effectTimeoutRef.current) {
      clearTimeout(effectTimeoutRef.current);
      effectTimeoutRef.current = null;
    }

    cardAnimationRef.current?.reset();
    setDisplay('');
    revealFinishedRef.current = false;

    if (hasReveal && revealVideo.videoRef?.current) {
      const video = revealVideo.videoRef.current;
      video.pause();
      video.currentTime = 0;
    }

    if (hasEffect && effectVideo.videoRef?.current) {
      const video = effectVideo.videoRef.current;
      video.pause();
      video.currentTime = 0;
    }

    hideEffect();
    envelopeAnimation();
  }, [
    hasReveal,
    hasEffect,
    hideEffect,
    envelopeAnimation,
    effectVideo.videoRef?.current,
    revealVideo.videoRef?.current,
  ]);

  const {meganavHeight} = useScrollToMeganav({
    isViewInvitationPage,
    scrollToPhotos,
    onReady: envelopeAnimation,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!hasReveal) {
      onCanPlayAnimation();
    }
    firstRender.current = false;
    hideEffect();

    // Position: sticky won't work here so we're simulating the effect
    const forceOverlayTop = () => {
      if (window.scrollY < invitationRootTop) {
        setOverlayTop(`${invitationRootTop - window.scrollY}px`);
      } else {
        setOverlayTop('0px');
      }
    };
    window.addEventListener('scroll', forceOverlayTop);
    return () => window.removeEventListener('scroll', forceOverlayTop);
  }, []);

  const handleScroll = useCallback(() => {
    if (!isMediumAndUp) {
      if (designTemplateRef?.current && meganavHeight && meganavHeight > 0) {
        const offset = designTemplateRef.current.offsetHeight / 5;
        const {scrollY} = window;
        if (scrollY > meganavHeight && scrollY < meganavHeight + offset) {
          designTemplateRef.current.style.marginTop = `-${(scrollY - meganavHeight) / 2}px`;
          designTemplateRef.current.style.marginBottom = `-${(scrollY - meganavHeight) / 2}px`;
        }
      }
    }
  }, [isMediumAndUp, designTemplateRef, meganavHeight]);

  const handleResize = useCallback(() => {
    if (!hasPlayedRef.current && isMediumAndUp) replayAnimation();
  }, [isMediumAndUp, replayAnimation]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, handleResize]);

  const overlayClass = isViewInvitationPage
    ? 'overlay-animation--view-invitation-overlay'
    : 'overlay-animation--preview-invitation-overlay';

  const effectAnimation = useMemo(
    () => (
      <div
        className={overlayClass}
        style={{top: isViewInvitationPage ? overlayTop : undefined, display}}
      >
        <OverlayAnimation
          eventOptionsDesign={eventOptionsDesign}
          effectVideo={effectVideo as any}
          hideOnLoad
          onEnded={() => setDisplay('none')}
          portalToBody={false}
        />
      </div>
    ),
    [eventOptionsDesign, effectVideo, overlayTop, isViewInvitationPage, display, overlayClass]
  );

  const revealAnimation = useMemo(
    () => (
      <div
        className={overlayClass}
        style={{top: isViewInvitationPage ? overlayTop : undefined, display}}
      >
        <OverlayAnimation
          eventOptionsDesign={eventOptionsDesign}
          revealVideo={revealVideo as any}
          onCanPlay={onCanPlayAnimation}
          onEnded={() => {
            revealFinishedRef.current = true;
          }}
          autoPlay
          portalToBody={isViewInvitationPage}
        />
      </div>
    ),
    [
      eventOptionsDesign,
      revealVideo,
      overlayTop,
      isViewInvitationPage,
      display,
      onCanPlayAnimation,
      overlayClass,
    ]
  );

  return (
    <>
      <CardAnimation
        ref={cardAnimationRef}
        isWebview={isWebview}
        fabricArt={fabricArt}
        noAnimation={!showAnimation}
        hasPlayedRef={hasPlayedRef}
        onReplay={replayAnimation}
        setAnimationComplete={setAnimationComplete}
      />
      {hasEffect && isViewInvitationPage && effectAnimation}
      {hasReveal && isViewInvitationPage && revealAnimation}
    </>
  );
};
